import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@shared/store/authStore';
import { getTrainerConfig } from '@shared/config/trainerTypes';
import { useTrainerBookings } from '@shared/hooks/useTrainerBookings';

export const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const trainer = user;

  // Получаем конфигурацию для типа тренера
  const trainerConfig = getTrainerConfig(trainer?.specName || 'Тренер');

  // Загружаем реальные данные записей
  const { upcomingBookings, stats, isLoading } = useTrainerBookings();

  const [currentTime, setCurrentTime] = useState(new Date());

  // Обновляем время каждую минуту
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const formatDate = (date: Date) => {
    const days = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  // Данные загружаются через useTrainerBookings hook

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Приветствие */}
      <div className="bg-slate-900/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white shadow-2xl shadow-amber-500/40 border-2 border-amber-400/70">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              Добрый день, {trainer?.firstName}!
            </h2>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-lg sm:text-xl font-semibold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">{formatTime(currentTime)}</p>
            <p className="text-slate-300 text-xs sm:text-sm">{formatDate(currentTime)}</p>
          </div>
        </div>
      </div>

      {/* Статистика за день */}
      <div>
        <h3 className="text-base sm:text-lg font-bold text-white/90 mb-3 sm:mb-4">Сегодня</h3>
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div className="bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg border border-blue-500/20">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <p className="text-[10px] sm:text-sm text-slate-300/80 mb-1 sm:mb-0">Всего записей</p>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-white">{stats.todayBookings}</p>
          </div>

          <div className="bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg border border-green-500/20">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <p className="text-[10px] sm:text-sm text-slate-300/80 mb-1 sm:mb-0">Завершено</p>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-white">{stats.completedToday}</p>
          </div>

          <div className="bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg border border-orange-500/20">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
              <p className="text-[10px] sm:text-sm text-slate-300/80 mb-1 sm:mb-0">Предстоит</p>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-white">{stats.upcomingToday}</p>
          </div>
        </div>
      </div>

      {/* Ближайшие записи/занятия/бронирования (адаптивно) */}
      <div>
        <div className="flex justify-between items-center mb-3 sm:mb-4">
          <h3 className="text-base sm:text-lg font-bold text-white/90">{trainerConfig.labels.bookingTitle}</h3>
          <Link to="/trainer/schedule" className="text-xs sm:text-sm text-amber-400 hover:text-amber-300 font-medium transition-colors">
            Все записи →
          </Link>
        </div>

        {isLoading ? (
          <div className="bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center shadow-lg border border-slate-700/50">
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-slate-700/50 rounded-full flex items-center justify-center mb-4 animate-pulse">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-slate-300 text-sm sm:text-base">Загрузка записей...</p>
          </div>
        ) : upcomingBookings.length === 0 ? (
          <div className="bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center shadow-lg border border-slate-700/50">
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-slate-700/50 rounded-full flex items-center justify-center mb-4">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-slate-300 mb-2 text-sm sm:text-base">{trainerConfig.labels.emptyStateMessage}</p>
            <p className="text-xs sm:text-sm text-slate-500">Вы свободны!</p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {upcomingBookings.map((booking: any) => (
              <div key={booking.id} className="bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-lg border border-amber-500/20 hover:border-amber-500/40 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm sm:text-base truncate">{booking.clientName}</p>
                    <p className="text-xs sm:text-sm text-slate-300/80 truncate">{booking.serviceName}</p>
                    {trainerConfig.features.showParticipants && booking.participants && (
                      <p className="text-[10px] sm:text-xs text-amber-400/70 mt-1">
                        {booking.participants.current}/{booking.participants.max} участников
                      </p>
                    )}
                  </div>
                  <div className="text-right ml-3">
                    <p className="font-semibold text-white text-sm sm:text-base">{booking.timeFrom}</p>
                    <p className="text-xs sm:text-sm text-slate-400">{booking.duration} мин</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Мои услуги */}
      {trainer?.services && trainer.services.length > 0 && (
        <div>
          <h3 className="text-base sm:text-lg font-bold text-white/90 mb-3 sm:mb-4">Мои услуги</h3>
          <div className="bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-slate-700/50">
            <div className="space-y-3 sm:space-y-4">
              {trainer.services.slice(0, 5).map((service) => (
                <div key={service.id} className="flex items-center justify-between pb-3 sm:pb-4 border-b border-slate-700/50 last:border-0 last:pb-0">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm sm:text-base truncate">{service.name}</p>
                    <p className="text-xs sm:text-sm text-slate-400">{service.duration} минут</p>
                  </div>
                  <div className="text-right ml-3">
                    <p className="font-bold text-amber-400 text-sm sm:text-base">{service.price}₽</p>
                  </div>
                </div>
              ))}
            </div>
            {trainer.services.length > 5 && (
              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-700/50">
                <p className="text-xs sm:text-sm text-slate-500 text-center">
                  И еще {trainer.services.length - 5} услуг...
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
